import { FC, useCallback, useRef, useState } from 'react';
import { faChevronLeft, faChevronRight, faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { i18nTB } from 'utils/shared/utils';
import { images } from 'constants/generic';

const TESTIMONIALS = [
  {
    initials: 'שד',
    name: 'שלומי דוידי',
    location: 'תאילנד, בנגקוק',
    text: 'Flyeast תכננו לנו את החופשה המשפחתית בצורה מושלמת — מלונות, טיסות וליווי אישי בעברית לאורך כל הדרך. ממליצים בחום!',
    avatarColor: '#7eb8c9',
  },
  {
    initials: 'מר',
    name: 'מאיה רוזן',
    location: 'תאילנד, פוקט',
    text: 'מקצועיים, קשובים ובאמת מותאמים למה שרצינו. כל פרט טופל מההתחלה ועד הסוף.',
    avatarColor: '#c4c4c4',
  },
] as const;

const TripBuilderClientTestimonials2: FC = () => {
  
  const { i18n } = useTranslation(undefined, { i18n: i18nTB });
  const isRtl = i18n.language === 'he';
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const getVisibleIndex = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return 0;
    }

    const trackRect = track.getBoundingClientRect();
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    Array.from(track.children).forEach((child, index) => {
      const rect = child.getBoundingClientRect();
      const distance = Math.abs(rect.left - trackRect.left);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }, []);

  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    const normalizedIndex =
      ((index % TESTIMONIALS.length) + TESTIMONIALS.length) % TESTIMONIALS.length;
    const card = track.children[normalizedIndex] as HTMLElement | undefined;

    card?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    setActiveIndex(normalizedIndex);
  }, []);

  const scrollByStep = useCallback(
    (direction: 1 | -1) => {
      scrollToIndex(activeIndex + direction);
    },
    [activeIndex, scrollToIndex],
  );

  const onTrackScroll = useCallback(() => {
    setActiveIndex(getVisibleIndex());
  }, [getVisibleIndex]);

  return (
    <section className="tb2-testimonials" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="tb2-testimonials-wrap">
        {!!images?.elephant && (
          <img className="tb2-testimonials-decor" src={images.elephant} alt="" aria-hidden="true" />
        )}

        <div className="tb2-testimonials-panel">
          <h2 className="tb2-testimonials-title">חשוב לנו מה אומרים עלינו...</h2>
          <p className="tb2-testimonials-subtitle">המלצות של לקוחות על Flyeast</p>

          <div className="tb2-testimonials-track" ref={trackRef} onScroll={onTrackScroll}>
            {TESTIMONIALS.map((testimonial, index) => (
              <article
                className={
                  'tb2-testimonials-card' + (activeIndex === index ? ' tb2-testimonials-card-featured' : '')
                }
                dir="rtl"
                key={testimonial.name}
              >
                <div className="tb2-testimonials-card-head">
                  <div className="tb2-testimonials-card-user">
                    <span
                      className="tb2-testimonials-card-avatar"
                      aria-hidden="true"
                      style={{ backgroundColor: testimonial.avatarColor }}
                    >
                      {testimonial.initials}
                    </span>
                    <div className="tb2-testimonials-card-meta">
                      <strong>{testimonial.name}</strong>
                      <span>{testimonial.location}</span>
                    </div>
                  </div>
                  <div className="tb2-testimonials-card-rating">
                    <span>5</span>
                    <FontAwesomeIcon icon={faStar} aria-hidden="true" />
                  </div>
                </div>
                <p className="tb2-testimonials-card-text">{testimonial.text}</p>
              </article>
            ))}
          </div>

          <div className="tb2-testimonials-controls">
            <div className="tb2-testimonials-dots" role="tablist" aria-label="המלצות של לקוחות">
              {TESTIMONIALS.map((testimonial, index) => (
                <button
                  key={testimonial.name}
                  type="button"
                  role="tab"
                  className={
                    'tb2-testimonials-dot' + (activeIndex === index ? ' tb2-testimonials-dot-active' : '')
                  }
                  aria-label={`${index + 1}`}
                  aria-selected={activeIndex === index}
                  onClick={() => scrollToIndex(index)}
                />
              ))}
            </div>

            <div className="tb2-testimonials-arrows">
              <button
                type="button"
                className="tb2-testimonials-arrow tb2-testimonials-arrow-prev"
                aria-label="ביקורת קודמת"
                onClick={() => scrollByStep(-1)}
              >
                <FontAwesomeIcon icon={isRtl ? faChevronRight : faChevronLeft} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="tb2-testimonials-arrow tb2-testimonials-arrow-next"
                aria-label="ביקורת הבאה"
                onClick={() => scrollByStep(1)}
              >
                <FontAwesomeIcon icon={isRtl ? faChevronLeft : faChevronRight} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TripBuilderClientTestimonials2;
